/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useSelector } from "react-redux";
import { initialProfile } from "../../../components/helpers";
import { useGetThresholdCrossingSettingProfileQuery, useDeleteThresholdCrossingSettingProfileMutation} from "../../../store/api/admin/adminApiSlice";
import { IThresholdCrossingSettingProfile } from "../../../store/api/admin/types";
import { useStoreDispatch } from "../../../store/hooks";
import { useEffect, useState, useRef } from "react";
import { API, DIALOGOPENFOR } from "../../../globals/globals";
import { selectedThresholdCrossingSettingProfileSelector,selectedThresholdCrossingSettingActionSelector,selectedThresholdCrossingSettingScheduleSelector, selectedThresholdCrossingSettingRuleSelector,isAddEditDeleteThresholdCrossingSettingProfileSelector,isAddEditDeleteThresholdCrossingSettingActionSelector } from "../../../store/api/admin/selectors";
import { setThresholdCrossingSettingProfileModelOpenMode,setThresholdCrossingSettingActionModelOpenMode,setThresholdCrossingSettingScheduleModelOpenMode, setThresholdCrossingSettingRuleModelOpenMode, setSelectedThresholdCrossingSettingProfile, setSelectedThresholdCrossingSettingSchedule,setIsAddEditDeleteThresholdCrossingSettingProfile } from "../../../store/api/admin/reducers";
import { constStr } from "../../../globals/constants";
import { basicDialogSelector } from "../../../store/helpers/helperSelectors";
import { setBasicDialogOpenFor, setConfirmDialog, setOpenCustomDailog, setIsLoadingStatus, setOpenToast } from "../../../store/helpers/helperReducers";
import { isEqualString, errorMessage, getMissingValues,titleCase } from "../../../utils/helpers";

interface IPayload {
  [key: string]: string;
}

export const useProfileList = () => {
  const dispatch = useStoreDispatch();
  //const { profileList, refetchProfiles } = useProfileDisplay();
  const { data : profileList = [], isLoading, refetch } = useGetThresholdCrossingSettingProfileQuery();
  
  const [selectedProfile, setSelectedProfile] = useState<IThresholdCrossingSettingProfile>(initialProfile);
  const [errMsg, setErrMsg] = useState<IPayload>({});
  const [selectedId, setSelectedId] = useState<number>(0);
  const [newlyCreatedProfileId, setNewlyCreatedProfileId] = useState<number | null>(null);
  const previousProfileListRef = useRef<IThresholdCrossingSettingProfile[]>([]);

  // // Force a refetch first thing
  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!profileList || profileList.length === 0) return;
    
    // Check if a new profile was created
    if (newlyCreatedProfileId !== null) {
      const newProfile = profileList.find(p => p.id === newlyCreatedProfileId);
      if (newProfile) {
        setSelectedProfile(newProfile);
        setSelectedId(newProfile.id);
        setNewlyCreatedProfileId(null);
        return;
      }
    }
    
    // Auto-select first profile if only one exists or if no profile is currently selected
    if (profileList.length === 1 || selectedId === 0) {
      const sortedProfiles = sortedProfileList(profileList);
      const firstProfile = profileList.length === 1 ? profileList[0] : sortedProfiles[0];
      setSelectedProfile(firstProfile);
      setSelectedId(firstProfile.id);
    }
    
    previousProfileListRef.current = profileList;
  }, [profileList, newlyCreatedProfileId]);

  

  const sortedProfileList = (profileList : IThresholdCrossingSettingProfile[])=> {
    return  [...profileList]
              .sort((a, b) => a.name.localeCompare(b.name));
  }
  
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const [deleteThresholdCrossingSettingProfile, deleteThresholdCrossingSettingProfileStatus] = useDeleteThresholdCrossingSettingProfileMutation();
  const deleteThresholdCrossingSettingProfileClickAction = async (actionName: string,profile: IThresholdCrossingSettingProfile) => {
      if (!actionName) return;
      if (actionName.match(constStr.yesDelete.toLowerCase())) {
          if (basicDialogOpenFor === DIALOGOPENFOR.DELETETHRESHOLDCROSSINGSETTINGPROFILE) {
           try {
              let response: any;
              let messageStr = `The ${profile?.name} profile has been deleted.`;
                response = await deleteThresholdCrossingSettingProfile(profile?.id);
                if (response?.error) throw response.error;
                if (response?.data?.status === 204) {
                  dispatch(setIsAddEditDeleteThresholdCrossingSettingProfile(true));
                  dispatch(
                    setOpenToast({
                      openToast: true,
                      toastType: "success",
                      message: `${messageStr}`,
                    })
                  );
                }
            } catch (error: any) {
              dispatch(setIsAddEditDeleteThresholdCrossingSettingProfile(false));
              dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
            }
          }
      }
    };

   const isAddEditDeleteThresholdCrossingSettingProfile=useSelector(isAddEditDeleteThresholdCrossingSettingProfileSelector);
    useEffect(() => {
       const LoadProfileData = async () => {
         if (isAddEditDeleteThresholdCrossingSettingProfile) {
           const {data : newData = [] } = await refetch();
           sortedProfileList(newData);
           dispatch(setIsAddEditDeleteThresholdCrossingSettingProfile(false));
         } 
       };
       LoadProfileData();
     }, [
       isAddEditDeleteThresholdCrossingSettingProfile,
       refetch
     ]);
    
  const deleteProfile = async (profile: IThresholdCrossingSettingProfile) => {
    try {
      const messageStr = `The deletion process for ${profile?.name} has been initiated.`;
      const response: any = await deleteThresholdCrossingSettingProfile(profile?.id);
      if (response?.error) throw response.error;
      if (response?.data?.status === 204) {
        dispatch(setIsAddEditDeleteThresholdCrossingSettingProfile(true));
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: messageStr,
          })
        );
      }
    } catch (error: any) {
      dispatch(setIsAddEditDeleteThresholdCrossingSettingProfile(false));
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return {
    profileList,
    sortedProfileList,
    selectedProfile,
    setSelectedProfile,
    selectedId,
    setSelectedId,
    deleteThresholdCrossingSettingProfileClickAction,
    deleteProfile,
    setNewlyCreatedProfileId
  };
};